import { TextField } from '@mui/material';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import SessionFilter from '../Components/Global_Components/common/SessionFilter';
import BasicTable from '../Components/Global_Components/reacttable/table';
import { fetchPatientBill, getArchiveData } from '../Redux/Thunk/Actions';
import '../styles/Patients.scss';
import { parseBillHTML } from '../utils/billParser';
import { downloadExcel, printPDF } from '../utils/printUtils';

dayjs.extend(localizedFormat);

const ScanReport = ({loaderchange, popupalert, popuptext}) => {
  const dispatch = useDispatch();
  const userdata = useSelector(state => state.counter.userdata);
  const sideactive = useSelector(state => state.counter.sidebarnav);

  // State management
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({pageIndex: 1, pageSize: 10});
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [showArchive, setShowArchive] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [datacount, setdatacount] = useState(10);
  const [session, setSession] = useState('');

  // Derived values
  const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
  const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');
  const isAdmin = userdata.role === 'Admin';

  const headers = {
    serialNo: 'S.NO',
    ascasNo: 'ASCAS NO',
    patientName: 'PATIENTS NAME',
    age: 'AGE',
    gender: 'GENDER',
    scanType: 'TYPE OF SCAN',
    consultant: 'CONSULTANT',
    scanDate: 'SCAN DATE',
    scanTime: 'SCAN TIME',
    billAmount: 'BILL AMOUNT',
    remarks: 'REMARKS'
  };

  const subHeaders = {
    billTotal: 'SCAN BILL'
  };

  const fetchAllData = async (isArchive = false) => {
    loaderchange(true);
    const action = isArchive ? getArchiveData : fetchPatientBill;
    const initialPayload = {
      page: 1,
      page_size: 10,
      search: searchQuery,
      from_date: startDate,
      to_date: endDate,
      range: true,
      session: session
    };

    const initialResponse = await dispatch(action(initialPayload));

    if (initialResponse?.payload?.status?.code === 200) {
      const {data, total_count} = initialResponse.payload;
      setdatacount(total_count);
      const totalPages = Math.ceil(total_count / 10);
      const promises = [];
      for (let i = 1; i <= totalPages; i++) {
        const payload = {
          page: i,
          page_size: 10,
          search: searchQuery,
          from_date: startDate,
          to_date: endDate,
          range: true,
          session: session
        };
        promises.push(dispatch(action(payload)));
      }

      const responses = await Promise.all(promises);
      const allBills = responses.flatMap(response => response.payload.data.data || response.payload.data);
      const scanReports = allBills
        .flatMap(bill => {
          if (bill.report_template) {
            const parsed = parseBillHTML(bill.report_template);
            return parsed.scan.map(item => ({
              ...item,
              patient_id: bill.patient_id,
              created_at: bill.created_at,
              consultant: item.consultant || bill.patient_id?.doctor_name || '',
              scanDate: item.scanDate || dayjs(bill.created_at).format('YYYY-MM-DD'),
              scanTime: item.scanTime || dayjs(bill.created_at).format('HH:mm')
            }));
          }
          return [];
        })
        .filter(item => item.description && item.amount);
      setTableData(scanReports);

      if (isInitialLoad) setIsInitialLoad(false);
    } else {
      popupalert(true);
      popuptext('No Data Available');
      setTimeout(() => popupalert(false), 2000);
    }
    loaderchange(false);
  };

  useEffect(() => {
    fetchAllData(showArchive);
  }, [searchQuery, startDate, endDate, showArchive, session]);

  const [billTotal] = useMemo(() => {
    let bill = 0;
    tableData.forEach(item => {
      bill += Math.round(Number(item.amount) || 0);
    });
    return [bill];
  }, [tableData]);

  const currentPageData = useMemo(() => {
    const startIndex = (pagination.pageIndex - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, pagination]);

  const [currentPageTotal] = useMemo(() => {
    let total = 0;
    currentPageData.forEach(item => {
      total += Math.round(Number(item.amount) || 0);
    });
    return [total];
  }, [currentPageData]);

  const handlePageSizeChange = useCallback(pageSize => {
    setPagination(prev => {
      if (prev.pageSize === pageSize) return prev;
      return {...prev, pageSize, pageIndex: 1};
    });
  }, []);

  const handlePageIndexChange = useCallback(pageIndex => {
    setPagination(prev => {
      if (prev.pageIndex === pageIndex) return prev;
      return {...prev, pageIndex};
    });
  }, []);

  const handleDateChange = newDates => {
    setDateRange(newDates);
    if (newDates[0] && newDates[1]) {
      fetchAllData();
    }
  };

  const handleSessionChange = useCallback(newSession => {
    setSession(newSession);
    setPagination(prev => ({...prev, pageIndex: 1}));
  }, []);

  const formattedData = tableData.map((item, index) => ({
    serialNo: index + 1,
    ascasNo: item.patient_id?.p_uhid || '--',
    patientName: `${item.patient_id?.titles || ''} ${item.patient_id?.first_name || ''} ${item.patient_id?.last_name || ''}`.trim(),
    age: item.patient_id?.age || '--',
    gender: item.patient_id?.gender || '--',
    scanType: item.description,
    consultant: item.consultant || '--',
    scanDate: item.scanDate ? dayjs(item.scanDate).format('DD-MM-YYYY') : '--',
    scanTime: item.scanTime || '--',
    billAmount: Math.round(item.amount) || 0,
    remarks: item.remark
  }));

  const handlePrintPdf = () => {
    const subRow = [billTotal];
    printPDF({title: 'Scan Report', headers, data: formattedData, subHeaders, subRow});
  };
  const handleDownload = () => {
    const subRow = ['', '', '', '', '', '', '', '', billTotal, ''];
    downloadExcel({title: 'Scan Report', headers, data: formattedData, subRow});
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: 'SI No.',
        accessor: '',
        Cell: row => {
          return (
            <>
              <span>
                <div style={{marginTop: '3px'}} data-tooltip-id='my-tooltip' data-tooltip-content={String(1 + Number(row.row.id))}>
                  {1 + Number(row.row.id)}.
                </div>
              </span>
            </>
          );
        }
      },
      {
        Header: 'ASCAS No',
        accessor: 'patient_id',
        Cell: ({row}) => <TooltipContent>{row.original.patient_id?.p_uhid || '--'}</TooltipContent>
      },
      {
        Header: 'Patient Name',
        accessor: 'patient_id',
        Cell: ({row}) => (
          <TooltipContent>
            {row.original.patient_id?.titles}.{row.original.patient_id?.first_name} {row.original.patient_id?.last_name}
          </TooltipContent>
        )
      },
      {
        Header: 'Age',
        accessor: 'age',
        Cell: ({row}) => <TooltipContent>{row.original.patient_id?.age || '--'}</TooltipContent>
      },
      {
        Header: 'Gender',
        accessor: 'gender',
        Cell: ({row}) => <TooltipContent>{row.original.patient_id?.gender || '--'}</TooltipContent>
      },
      {
        Header: 'Type of Scan',
        accessor: 'description',
        Cell: ({value}) => <TooltipContent>{value}</TooltipContent>
      },
      {
        Header: 'Consultant',
        accessor: 'consultant',
        Cell: ({value}) => <TooltipContent>{value || '--'}</TooltipContent>
      },
      {
        Header: 'Scan Date',
        accessor: 'scanDate',
        Cell: ({value}) => <TooltipContent>{value ? dayjs(value).format('DD-MM-YYYY') : '--'}</TooltipContent>,
        Footer: () => <span>TOTAL</span>
      },
      {
        Header: 'Scan Time',
        accessor: 'scanTime',
        Cell: ({value}) => <TooltipContent>{value || '--'}</TooltipContent>
      },
      {
        Header: 'Scan Amount',
        accessor: 'amount',
        Cell: ({value}) => <TooltipContent>{Math.round(value) || '--'}</TooltipContent>,
        Footer: () => <span>{currentPageTotal && Math.round(currentPageTotal)}</span>
      },

      {
        Header: 'Remarks',
        accessor: 'remark',
        Cell: ({value}) => <TooltipContent>{value}</TooltipContent>
      }
    ],
    [showArchive, currentPageTotal]
  );

  return (
    <div className={`maincontainer-patient ${sideactive ? 'maincontainer-patient-sa' : ''}`}>
      <div className='patient-header'>
        <Breadcrumbs />
        <div className='add_btn' style={{display: 'flex', gap: '10px'}}>
          <button onClick={handlePrintPdf} className='btn_1'>
            Print
          </button>
          <button onClick={handleDownload} className='btn_1'>
            Download
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className='billableCard'>
          <p>
            {startDate === dayjs().format('YYYY-MM-DD') && endDate === dayjs().format('YYYY-MM-DD') ? "Today's Billing Overview" : 'Billing Overview'}
            &nbsp;:&nbsp;
            <span>{billTotal ?? 0}&nbsp;Rs</span>
          </p>
        </div>
      )}

      <div className='card'>
        <div className='card-body'>
          <div className='table-controls' style={{display: 'flex', justifyContent: 'space-between'}}>
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            <div style={{display: 'flex', gap: '10px'}}>
              <SessionFilter value={session} onChange={handleSessionChange} />
              <DateRangeFilter value={dateRange} onChange={handleDateChange} />
            </div>
          </div>

          <BasicTable
            columns={tableColumns}
            data={currentPageData}
            pagesize={handlePageSizeChange}
            pageindex={handlePageIndexChange}
            dtpagesize={pagination.pageSize}
            dtpageindex={pagination.pageIndex}
            datacount={tableData.length}
          />
        </div>
      </div>

      <ReactTooltip id='table-tooltip' arrowColor='transparent' place='bottom-start' delayShow={200} className='custom-tooltip' />
    </div>
  );
};

// Sub-components for better readability
const TooltipContent = ({children}) => (
  <div data-tooltip-id='table-tooltip' data-tooltip-content={children}>
    {children}
  </div>
);

const Breadcrumbs = () => (
  <div className='row'>
    <div className='col'>
      <h3 className='page-title'>Scan Report</h3>
      <ul className='breadcrumb'>
        <li className='breadcrumb-item'>
          <a href='/'>Dashboard</a>
        </li>
        <li className='breadcrumb-item active'>Scan Report</li>
      </ul>
    </div>
  </div>
);

const SearchInput = ({value, onChange}) => (
  <div className='input-group rounded' style={{width: '250px', height: '40px'}}>
    <input
      type='search'
      class='form-control rounded'
      aria-label='Search'
      aria-describedby='search-addon'
      value={value}
      style={{
        height: '40px'
      }}
      onChange={e => onChange(e.target.value)}
      placeholder='Search...'
      maxLength={100}
    />
    <span class='input-group-text border-0 bg-transparent p-0' id='search-addon'>
      <i
        class='bi bi-search'
        style={{
          color: '#1b5a90',
          width: '20px',
          height: '20px',
          marginLeft: '10px'
        }}></i>
    </span>
  </div>
);

const DateRangeFilter = ({value, onChange}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DateRangePicker
      value={value}
      onChange={onChange}
      maxDate={dayjs()}
      renderInput={(startProps, endProps) => (
        <>
          <FormattedDateField {...startProps} />
          <FormattedDateField {...endProps} />
        </>
      )}
    />
  </LocalizationProvider>
);

const FormattedDateField = props => <TextField {...props} InputLabelProps={{shrink: true}} inputProps={{...props.inputProps, value: dayjs(props.inputProps.value).format('DD-MM-YYYY')}} />;

export default ScanReport;
